import React, { useState, useEffect, useMemo } from 'react';
import {
    Card,
    Table,
    Form,
    InputGroup,
    Spinner,
    Row,
    Col,
    Pagination,
} from 'react-bootstrap';
import {
    useReactTable,
    getCoreRowModel,
    getSortedRowModel,
    flexRender,
} from '@tanstack/react-table';

function OrdersPage({ onBack }) {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pagination, setPagination] = useState({
        count: 0,
        next: null,
        previous: null,
        currentPage: 1,
        pageSize: 20,
    });

    // status filter + sorting state
    const [statusFilter, setStatusFilter] = useState('all');
    const [sorting, setSorting] = useState([{ id: 'created_at', desc: true }]);
    const [searchQuery, setSearchQuery] = useState('');

    // define your columns once
    const columns = useMemo(
        () => [
            {
                header: 'Order #',
                accessorKey: 'order_number',
                cell: info => `#${info.getValue()}`,
            },
            {
                header: 'Date',
                accessorKey: 'created_at',
                cell: info => new Date(info.getValue()).toLocaleString(),
            },
            {
                header: 'User',
                accessorFn: row =>
                    `${row.user?.first_name || ''} ${row.user?.last_name || ''}`,
            },
            {
                header: 'Status',
                accessorKey: 'status',
                cell: info =>
                    info
                        .getValue()
                        ?.charAt(0)
                        .toUpperCase() + info.getValue()?.slice(1),
            },
            {
                header: 'Items',
                accessorFn: row => row.items || [],
                cell: info => (
                    <ul className="mb-0 ps-3">
                        {(info.getValue() || []).map(item => (
                            <li key={item.id}>
                                {item.quantity}× {item.product?.name || 'Unknown'} @ $
                                {parseFloat(item.price || 0).toFixed(2)}
                            </li>
                        ))}
                    </ul>
                ),
            },
            {
                header: 'Subtotal',
                accessorKey: 'subtotal',
                cell: info => `$${parseFloat(info.getValue() || 0).toFixed(2)}`,
            },
            {
                header: 'Total',
                accessorKey: 'total_amount',
                cell: info => `$${parseFloat(info.getValue() || 0).toFixed(2)}`,
            },
        ],
        []
    );

    // create the table instance
    const table = useReactTable({
        data: orders || [],
        columns,
        state: { sorting },
        onSortingChange: setSorting,
        manualSorting: true,               // server-side
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
    });

    // fetch (with server-side ordering/filtering)
    useEffect(() => {
        async function fetchOrders() {
            setLoading(true);
            try {
                const params = new URLSearchParams();
                if (statusFilter !== 'all') params.set('status', statusFilter);
                if (sorting.length) {
                    const { id, desc } = sorting[0];
                    params.set('ordering', (desc ? '-' : '') + id);
                }
                if (searchQuery) {
                    params.set('search', searchQuery);
                }
                params.set('page', pagination.currentPage);
                params.set('page_size', pagination.pageSize);
                const url = `/api/orders/order-manager/?${params.toString()}`;
                const res = await fetch(url, {
                    credentials: 'include',
                    headers: {
                        'Accept': 'application/json',
                        'Content-Type': 'application/json',
                    }
                });

                if (!res.ok) {
                    const errorData = await res.json().catch(() => ({}));
                    throw new Error(errorData.detail || res.statusText);
                }

                const data = await res.json();

                // Handle both paginated and non-paginated responses
                const orders = data.results || data;
                const count = data.count || orders.length;

                setOrders(orders);
                setPagination(prev => ({
                    ...prev,
                    count: count,
                    next: data.next,
                    previous: data.previous,
                }));
            } catch (err) {
                console.error('Error fetching orders:', err);
                setOrders([]);
            } finally {
                setLoading(false);
            }
        }
        fetchOrders();
    }, [statusFilter, sorting, pagination.currentPage, pagination.pageSize, searchQuery]);

    const handlePageChange = (pageNumber) => {
        setPagination(prev => ({ ...prev, currentPage: pageNumber }));
    };

    const handlePageSizeChange = (newSize) => {
        setPagination(prev => ({ ...prev, pageSize: parseInt(newSize), currentPage: 1 }));
    };

    const handleSearch = (e) => {
        setSearchQuery(e.target.value);
        setPagination(prev => ({ ...prev, currentPage: 1 })); // Reset to first page on search
    };

    // Calculate total pages
    const totalPages = Math.ceil(pagination.count / pagination.pageSize);

    // Generate pagination items
    const paginationItems = [];
    for (let number = 1; number <= totalPages; number++) {
        paginationItems.push(
            <Pagination.Item
                key={number}
                active={number === pagination.currentPage}
                onClick={() => handlePageChange(number)}
            >
                {number}
            </Pagination.Item>
        );
    }

    return (
        <Card className="my-4">
            <Card.Header>
                <h5 className="card-title">Orders</h5>
                <p className="mb-0">View and manage all orders here.</p>
            </Card.Header>
            <Card.Body>
                {loading && (
                    <div className="position-absolute top-50 start-50 translate-middle">
                        <Spinner animation="border" />
                    </div>
                )}
                <Row className="mb-3 g-2">
                    <Col xs={6} sm={4} md={3} lg={3}>
                        <InputGroup size="sm">
                            <InputGroup.Text>Status</InputGroup.Text>
                            <Form.Select
                                value={statusFilter}
                                onChange={e => setStatusFilter(e.target.value)}
                            >
                                <option value="all">All</option>
                                <option value="pending">Pending</option>
                                <option value="paid">Paid</option>
                                <option value="cancelled">Cancelled</option>
                            </Form.Select>
                        </InputGroup>
                    </Col>
                    <Col xs={6} sm={4} md={3} lg={3}>
                        <InputGroup size="sm">
                            <InputGroup.Text>Results</InputGroup.Text>
                            <Form.Select
                                value={pagination.pageSize}
                                onChange={e => handlePageSizeChange(e.target.value)}
                            >
                                <option value="10">10</option>
                                <option value="20">20</option>
                                <option value="50">50</option>
                                <option value="100">100</option>
                            </Form.Select>
                        </InputGroup>
                    </Col>
                    <Col xs={12} sm={4} md={6} lg={6}>
                        <InputGroup size="sm">
                            <InputGroup.Text>
                                <i className="bi bi-search"></i>
                            </InputGroup.Text>
                            <Form.Control
                                type="text"
                                placeholder="Search by name or email..."
                                value={searchQuery}
                                onChange={handleSearch}
                            />
                        </InputGroup>
                    </Col>
                </Row>

                <Table striped hover responsive>
                    <thead>
                        {table.getHeaderGroups().map(headerGroup => (
                            <tr key={headerGroup.id}>
                                {headerGroup.headers.map(header => (
                                    <th
                                        key={header.id}
                                        onClick={header.column.getToggleSortingHandler()}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        {!header.isPlaceholder && flexRender(
                                            header.column.columnDef.header,
                                            header.getContext()
                                        )}
                                        {{
                                            asc: ' ↑',
                                            desc: ' ↓',
                                        }[header.column.getIsSorted()] ?? ' ↕'}
                                    </th>
                                ))}
                            </tr>
                        ))}
                    </thead>
                    <tbody>
                        {table.getRowModel().rows.map(row => (
                            <tr key={row.id}>
                                {row.getVisibleCells().map(cell => (
                                    <td key={cell.id}>
                                        {flexRender(
                                            cell.column.columnDef.cell,
                                            cell.getContext()
                                        )}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </Table>

                {totalPages > 1 && (
                    <div className="d-flex justify-content-center mt-4">
                        <Pagination>
                            <Pagination.First
                                onClick={() => handlePageChange(1)}
                                disabled={pagination.currentPage === 1}
                            />
                            <Pagination.Prev
                                onClick={() => handlePageChange(pagination.currentPage - 1)}
                                disabled={pagination.currentPage === 1}
                            />
                            {paginationItems}
                            <Pagination.Next
                                onClick={() => handlePageChange(pagination.currentPage + 1)}
                                disabled={pagination.currentPage === totalPages}
                            />
                            <Pagination.Last
                                onClick={() => handlePageChange(totalPages)}
                                disabled={pagination.currentPage === totalPages}
                            />
                        </Pagination>
                    </div>
                )}
            </Card.Body>
        </Card>
    );
}

export default OrdersPage;
